import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SsUnitDetailModal, { UNIT_HUB_TABS, getUnitHubTabs } from '../../components/SsUnitDetailModal';
import { UnitHubInfoContent } from '../../components/UnitHubInfoContent';
import { unitDisplayTitle } from '../../utils/unitDisplay';
import { formatUnitIdShort, floorChildUnits } from '../../utils/unitIdFormat';
import { useMemo } from 'react';

/**
 * Admin SS Unit hub ?? single place for all unit ops fields + confirmed questions.
 * Same registry record returned by every unit API.
 */
export default function BuildingUnitDetail() {
  const { id: buildingId, unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tabParam = searchParams.get('tab') || 'system-info';
  const normalizedTab =
    tabParam === 'photos'
      ? 'update-info'
      : tabParam === 'checklist'
        ? 'set-inspection-list'
        : tabParam;
  const activeTab = UNIT_HUB_TABS.some((t) => t.id === normalizedTab) ? normalizedTab : 'system-info';

  const { data: building } = useQuery({
    queryKey: ['building', buildingId],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!buildingId,
  });

  const {
    data: unit,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ss-unit', unitId],
    queryFn: async () => {
      const res = await api.get(`/ss-units/${unitId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!unitId,
  });

  const { data: siblingUnits = [] } = useQuery({
    queryKey: ['building', buildingId, 'ss-units', 'all'],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/ss-units`, {
        params: { includeInactive: '1' },
      });
      return res.data?.data ?? [];
    },
    enabled: !!buildingId,
  });

  const fromSetup = location.state?.origin?.label === 'Select SS Units Setting';
  const setupListPath = location.state?.origin?.to;

  const backToList = () => {
    if (setupListPath) {
      navigate(setupListPath);
      return;
    }
    const ss = unit?.ss_code;
    navigate(
      ss
        ? `/buildings/${buildingId}/units?ss=${encodeURIComponent(ss)}`
        : `/buildings/${buildingId}/units`
    );
  };

  const floorUnits = useMemo(
    () => (unit ? floorChildUnits(unit, siblingUnits) : []),
    [unit, siblingUnits]
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['ss-unit', unitId]);
    queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
  };

  if (isLoading) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="Unit" />
        <p className="text-gray-500 mt-6">Loading unit…</p>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="Unit" />
        <p className="text-red-600 mt-6">{error?.response?.data?.error || 'Unit not found'}</p>
      </div>
    );
  }

  if (String(unit.building_id) !== String(buildingId)) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="Unit" />
        <p className="text-red-600 mt-6">This unit does not belong to this building.</p>
      </div>
    );
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={building?.name}
        title={
          fromSetup
            ? `SS ${formatUnitIdShort(unit.unique_id, unit.ss_code)}${
                unit.name || location.state?.setupTypeLabel
                  ? ` ${unit.name || location.state.setupTypeLabel}`
                  : ''
              }`
            : unitDisplayTitle(unit)
        }
        unitsSsCode={unit?.ss_code}
        infoTitle="Unit hub"
        infoChildren={
          <UnitHubInfoContent unitIdHint={unit.unique_id || unit.id} ssCode={unit.ss_code} />
        }
        tabs={getUnitHubTabs(unit)}
        activeTab={activeTab}
        onTabChange={(id) => setSearchParams({ tab: id }, { replace: true })}
      />
      <div className="mt-4">
        <SsUnitDetailModal
          asPage
          hubTab={activeTab}
          unit={unit}
          building={building || { id: buildingId }}
          usedUniqueIds={siblingUnits.map((u) => u.unique_id)}
          idSuffixOnly
          floorUnits={floorUnits}
          siblingUnits={siblingUnits}
          onOpenFloorUnit={(fu) => {
            navigate(`/buildings/${buildingId}/equipment-units/${fu.id}`, {
              state: {
                parentUnitId: unit.id,
                origin: {
                  to: `/buildings/${buildingId}/units/${unit.id}?tab=system-info`,
                  label: 'Set SS units',
                },
              },
            });
          }}
          onClose={backToList}
          onSaved={() => {
            invalidate();
          }}
          onDeleted={() => {
            invalidate();
            backToList();
          }}
        />
      </div>
    </div>
  );
}
